import { formatCurrency } from "@/lib/currency";
import { useEffect, useState } from "react";
import { AlertTriangle, TrendingUp, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useJobUsage } from "@/hooks/useJobUsage";
import { SUBSCRIPTION_TIERS, SubscriptionTierKey } from "@/lib/subscriptionTiers";
import { PlanChangeConfirmDialog } from "./PlanChangeConfirmDialog";
import { useBackendSubscription } from "@/hooks/useBackendSubscription";
import { useCompany } from "@/hooks/useCompany";
import { warningSeenKey } from "@/lib/jobLimit";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface JobLimitWarningProps {
  showUpgradeButton?: boolean;
}

export function JobLimitWarning({ showUpgradeButton = true }: JobLimitWarningProps) {
  const {
    currentMonthJobs,
    jobLimit,
    tier,
    subscriptionStatus,
    isAtLimit,
    isNear95,
    isNearLimit,
    percentageUsed,
    warningLevel,
    loading,
    jobsRemaining,
    planName,
    periodStart,
    periodEnd,
    source,
  } = useJobUsage();
  const { role } = useBackendSubscription();
  const { companyId } = useCompany();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showThresholdPopup, setShowThresholdPopup] = useState(false);

  const isOwnerForPopup = role === "owner";
  const crossedThreshold = warningLevel === "soft" || warningLevel === "strong";

  // 80% popup: once per company per billing period (server usage only).
  useEffect(() => {
    if (loading || !crossedThreshold || !isOwnerForPopup || !showUpgradeButton || !companyId || source !== "server") return;
    const key = warningSeenKey(companyId, periodStart);
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, new Date().toISOString());
    } catch {
      return; // storage unavailable: rely on the persistent banner only
    }
    setShowThresholdPopup(true);
  }, [loading, crossedThreshold, isOwnerForPopup, showUpgradeButton, companyId, source, periodStart]);

  if (loading || warningLevel === "none") return null;

  const isOwnerRole = role === "owner";
  const canUpgrade = showUpgradeButton && isOwnerRole;

  const currentTier = SUBSCRIPTION_TIERS[tier];
  const nextTier = tier === "starter" ? SUBSCRIPTION_TIERS.growth : tier === "growth" ? SUBSCRIPTION_TIERS.pro : null;
  const nextTierKey: SubscriptionTierKey | null = tier === "starter" ? "growth" : tier === "growth" ? "pro" : null;
  const isTrialing = subscriptionStatus === "trialing";

  // ── CRITICAL: 100% — Job limit reached ──
  if (warningLevel === "critical") {
    return (
      <>
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Job Limit Reached</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>
              You've used all {jobLimit} jobs included in your {currentTier.name} plan this month.
              {!isOwnerRole && " New jobs cannot be created until next month or your administrator upgrades the plan."}
            </p>
            {nextTier && nextTierKey && canUpgrade && (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    onClick={() => setShowConfirm(true)}
                    className="gap-2"
                  >
                    <TrendingUp className="h-4 w-4" />
                    Upgrade to {nextTier.name} ({formatCurrency(nextTier.price)}/mo)
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {nextTier.jobLimit ? `${nextTier.jobLimit} jobs/month` : "Unlimited jobs"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {isTrialing
                    ? "No charge today · Renews every 30 days after trial · Cancel anytime"
                    : "Charges today · Renews every 30 days · Cancel anytime"}
                </p>
              </div>
            )}
            {!isOwnerRole && (
              <p className="text-sm text-muted-foreground">
                Please contact your company administrator to upgrade the subscription.
              </p>
            )}
          </AlertDescription>
        </Alert>

        {nextTierKey && canUpgrade && (
          <PlanChangeConfirmDialog
            open={showConfirm}
            onOpenChange={setShowConfirm}
            targetTierKey={nextTierKey}
            isTrialing={isTrialing}
          />
        )}
      </>
    );
  }

  // ── STRONG: 95% — Almost at limit ──
  if (warningLevel === "strong") {
    // Only show to owners
    if (!isOwnerRole) return null;

    return (
      <>
        <Alert variant="destructive" className="mb-4 border-destructive/50 bg-destructive/5">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Almost at Job Limit</AlertTitle>
          <AlertDescription className="space-y-3">
            <p className="font-medium">
              {currentMonthJobs} of {jobLimit} jobs used — only {jobLimit! - currentMonthJobs} remaining this month.
            </p>
            <div className="flex items-center justify-between text-sm">
              <span>{currentMonthJobs} of {jobLimit} jobs</span>
              <span className="font-medium text-destructive">{Math.round(percentageUsed)}%</span>
            </div>
            <Progress value={percentageUsed} className="h-2" />
            {nextTier && nextTierKey && canUpgrade && (
              <div className="space-y-2">
                <Button
                  size="sm"
                  onClick={() => setShowConfirm(true)}
                  className="gap-2"
                >
                  <TrendingUp className="h-4 w-4" />
                  Upgrade to {nextTier.name} — {nextTier.jobLimit ? `${nextTier.jobLimit} jobs/month` : "Unlimited jobs"}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {isTrialing
                    ? "No charge today · Renews every 30 days after trial · Cancel anytime"
                    : "Charges today · Renews every 30 days · Cancel anytime"}
                </p>
              </div>
            )}
          </AlertDescription>
        </Alert>

        {nextTierKey && canUpgrade && (
          <PlanChangeConfirmDialog
            open={showConfirm}
            onOpenChange={setShowConfirm}
            targetTierKey={nextTierKey}
            isTrialing={isTrialing}
          />
        )}
      </>
    );
  }

  // ── SOFT: 80% — Approaching limit ──
  // Only show to owners
  if (!isOwnerRole) return null;

  return (
    <>
      <Alert className="mb-4 border-amber-500/50 bg-amber-500/5">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <AlertTitle className="text-amber-600">Approaching Job Limit</AlertTitle>
        <AlertDescription className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span>
              {currentMonthJobs} of {jobLimit} jobs used this month
            </span>
            <span className="font-medium">{Math.round(percentageUsed)}%</span>
          </div>
          <Progress value={percentageUsed} className="h-2" />
          {nextTier && nextTierKey && canUpgrade && (
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowConfirm(true)}
                className="gap-2"
              >
                <TrendingUp className="h-4 w-4" />
                Upgrade to {nextTier.name}
              </Button>
              <p className="text-xs text-muted-foreground">
                {isTrialing
                  ? "No charge today · Renews every 30 days after trial · Cancel anytime"
                  : "Charges today · Renews every 30 days · Cancel anytime"}
              </p>
            </div>
          )}
        </AlertDescription>
      </Alert>

      <AlertDialog open={showThresholdPopup} onOpenChange={setShowThresholdPopup}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You're approaching your job limit</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">{currentMonthJobs} of {jobLimit} jobs used on your {planName || currentTier.name} plan.</span>
              <span className="block">You have {jobsRemaining ?? (jobLimit! - currentMonthJobs)} jobs remaining{periodEnd ? ` until ${new Date(periodEnd).toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone: "Europe/London" })}` : ""}.</span>
              <span className="block">Upgrade your plan to increase your job allowance.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
            {nextTierKey && canUpgrade && (
              <Button onClick={() => { setShowThresholdPopup(false); setShowConfirm(true); }}>Upgrade Plan</Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {nextTierKey && canUpgrade && (
        <PlanChangeConfirmDialog
          open={showConfirm}
          onOpenChange={setShowConfirm}
          targetTierKey={nextTierKey}
          isTrialing={isTrialing}
        />
      )}
    </>
  );
}
